import React, { useEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { Form } from 'react-final-form';
import { FORM_ERROR, FormApi } from 'final-form';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';

import { Dialog } from 'components/common';
import { useAdminAccess } from 'contexts/adminAccess';
import { Checkbox, Input } from 'components/form';
import { required } from 'components/form/validationRules';
import { MemberDTO } from 'pages/users/types';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    width: '100%',
    maxWidth: 860,
  },
  dialogContent: {
    minWidth: theme.spacing(86),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  dialogTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dialogSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.5,
  },
  section: {
    marginTop: theme.spacing(1),
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },
  inputNoMargin: {
    marginTop: 0,
  },
  rolesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  infoBanner: {
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  infoBannerTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  loadingState: {
    minHeight: theme.spacing(18),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '16px',
    borderRadius: theme.spacing(1.5),
    border: `1px dashed ${theme.palette.divider}`,
  },
  loadingText: {
    color: theme.palette.text.secondary,
  },
  emptyState: {
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(3),
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  emptyStateTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  roleGroup: {
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
  },
  roleGroupHeader: {
    padding: theme.spacing(2, 2.5),
    backgroundColor: '#FAFAFC',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  groupCheckbox: {
    margin: 0,
    width: '100%',
  },
  groupLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  groupLabelTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  groupTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  groupTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  groupCode: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  groupDescription: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.5,
  },
  summaryChipIdle: {
    backgroundColor: '#F1F3F5',
    color: theme.palette.text.secondary,
  },
  summaryChipPartial: {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
  },
  summaryChipComplete: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
  permissionsList: {
    padding: theme.spacing(2, 2.5, 2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  permissionsListInner: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
  },
  permissionCheckbox: {
    margin: 0,
    width: '100%',
  },
  permissionLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  permissionTitle: {
    color: theme.palette.text.primary,
  },
  permissionCode: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  permissionEmpty: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontStyle: 'italic',
  },
}));

interface Props {
  open: boolean;
  initialValues?: MemberDTO;
  onClose: () => void;
  tenantId: string;
}

interface Role {
  description: string;
  name: string;
}

interface RolesResponse {
  description: string;
  name: string;
  roles: Role[];
}

interface FormValues {
  firstName: string;
  secondName: string;
  phone: string;
  email: string;
  position: string;
  roles: Record<string, boolean>;
}

const defaultValues: FormValues = {
  firstName: '',
  secondName: '',
  phone: '',
  email: '',
  position: '',
  roles: {},
};

const getSelectedPermissionsCount = (values: FormValues['roles'], groupRoles: Role[]) =>
  groupRoles.filter((role) => values?.[role.name]).length;

const getSelectionSummary = (selectedCount: number, totalCount: number) => {
  if (!totalCount) {
    return {
      label: 'No permissions',
      className: 'idle',
    };
  }

  if (!selectedCount) {
    return {
      label: 'No permissions selected',
      className: 'idle',
    };
  }

  if (selectedCount === totalCount) {
    return {
      label: `All ${totalCount} selected`,
      className: 'complete',
    };
  }

  return {
    label: `${selectedCount} of ${totalCount} selected`,
    className: 'partial',
  };
};

const SubUserRolesDialog = ({ open, initialValues, onClose, tenantId }: Props) => {
  const { canWrite } = useAdminAccess();
  const classes = useStyles();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const formRef = useRef<FormApi<FormValues>>();

  const [roles, setRoles] = useState<RolesResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const getRoles = async () => {
      try {
        setLoading(true);
        const { data } = await dataProvider.get('membersRoles', { tenantId });

        setRoles(Array.isArray(data) ? data : []);
      } catch (error: any) {
        notify(
          typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
          'warning'
        );
      } finally {
        setLoading(false);
      }
    };

    getRoles();
  }, [dataProvider, notify, tenantId, open]);

  useEffect(() => {
    if (!roles) {
      return;
    }

    if (formRef.current && initialValues) {
      const mappedRoles: Record<string, boolean> = {};
      const filteredMainRoles = roles
        .filter((mainRole) =>
          mainRole.roles.every((role) => initialValues.roles.includes(role.name))
        )
        .map((mainRole) => mainRole.name);

      initialValues.roles?.forEach((role) => {
        mappedRoles[role] = true;
      });
      filteredMainRoles?.forEach((role) => {
        mappedRoles[role] = true;
      });

      const newValues: FormValues = {
        firstName: initialValues.firstName,
        secondName: initialValues.secondName,
        phone: initialValues.phone,
        email: initialValues.email,
        position: initialValues.position,
        roles: mappedRoles,
      };

      formRef.current.reset(newValues);
    }
  }, [initialValues, roles]);

  const handleSubmitForm = async (values: FormValues) => {
    if (!canWrite) return;
    try {
      const selectedRoles: string[] = [];

      const mainRoles = roles.map((item) => item.name);

      for (const key in values.roles) {
        if (values.roles[key] && !mainRoles.includes(key)) {
          selectedRoles.push(key);
        }
      }

      if (!selectedRoles.length) {
        return { [FORM_ERROR]: 'Select at least one role' };
      }

      const requestData = {
        ...values,
        roles: selectedRoles,
      };

      let response;

      if (initialValues?.id) {
        response = await dataProvider.update('deleteOrEditMember', {
          tenantId,
          memberId: initialValues.id,
          data: requestData,
        });
      } else {
        response = await dataProvider.post('createMember', {
          tenantId,
          data: requestData,
        });
      }

      onClose();
      refresh();

      return response;
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  return (
    <Form<FormValues>
      initialValues={defaultValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, form, values, submitError }) => {
        formRef.current = form;

        const totalSelectedRoles = roles.reduce(
          (count, mainRole) => count + getSelectedPermissionsCount(values.roles, mainRole.roles),
          0
        );

        return (
          <Dialog
            readOnly={!canWrite}
            open={open}
            onCancel={onClose}
            title={
              <Box className={classes.dialogTitle}>
                <Typography variant="h6">
                  {!canWrite
                    ? 'View sub-user'
                    : initialValues
                      ? 'Edit sub-user'
                      : 'Create sub-user'}
                </Typography>
                <Typography className={classes.dialogSubtitle}>
                  Manage contact details and choose the permissions this team member should receive.
                </Typography>
              </Box>
            }
            onConfirm={handleSubmit}
            submitting={submitting}
            error={submitError}
            confirmText="Save"
            classes={{ content: classes.dialogContent, root: classes.dialogRoot }}>
            <Box className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>Profile details</Typography>
                <Typography className={classes.sectionDescription}>
                  These details are used to identify the sub-user in the member list and access
                  settings.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Input
                    name="firstName"
                    label="First name"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    name="secondName"
                    label="Last name"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    name="email"
                    label="Email"
                    validate={required}
                    inputProps={{ InputProps: { readOnly: !!initialValues } }}
                    classes={{ input: classes.inputNoMargin }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    name="phone"
                    label="Phone"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    name="position"
                    label="Position"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>Access permissions</Typography>
                <Typography className={classes.sectionDescription}>
                  Select a role group to grant every permission inside it, or choose individual
                  permissions one by one.
                </Typography>
              </Box>

              <Box className={classes.rolesContainer}>
                {!loading && !!roles.length && !totalSelectedRoles && (
                  <Box className={classes.infoBanner}>
                    <Typography className={classes.infoBannerTitle}>
                      No permissions selected yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Choose at least one permission before saving this sub-user.
                    </Typography>
                  </Box>
                )}

                {loading ? (
                  <Box className={classes.loadingState}>
                    <CircularProgress size={28} />
                    <Typography className={classes.loadingText}>
                      Loading available permission groups...
                    </Typography>
                  </Box>
                ) : !roles.length ? (
                  <Box className={classes.emptyState}>
                    <Typography className={classes.emptyStateTitle}>
                      No permission groups available
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Roles could not be loaded for this tenant, so no sub-user permissions can be
                      assigned right now.
                    </Typography>
                  </Box>
                ) : (
                  roles.map((mainRole) => {
                    const selectedCount = getSelectedPermissionsCount(values.roles, mainRole.roles);
                    const summary = getSelectionSummary(selectedCount, mainRole.roles.length);

                    const handleToggleMainRole = (value) => {
                      form.change(`roles.${mainRole.name}`, value);
                      mainRole.roles.forEach((role) => form.change(`roles.${role.name}`, value));
                    };

                    return (
                      <Box key={mainRole.name} className={classes.roleGroup}>
                        <Box className={classes.roleGroupHeader}>
                          <Checkbox
                            name={`roles.${mainRole.name}`}
                            onChange={handleToggleMainRole}
                            classes={{ root: classes.groupCheckbox }}
                            label={
                              <Box className={classes.groupLabel}>
                                <Box className={classes.groupLabelTopRow}>
                                  <Box className={classes.groupTitleBlock}>
                                    <Typography className={classes.groupTitle}>
                                      {mainRole.description}
                                    </Typography>
                                    <Typography className={classes.groupCode}>
                                      {mainRole.name}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={summary.label}
                                    className={
                                      summary.className === 'complete'
                                        ? classes.summaryChipComplete
                                        : summary.className === 'partial'
                                          ? classes.summaryChipPartial
                                          : classes.summaryChipIdle
                                    }
                                  />
                                </Box>
                                <Typography className={classes.groupDescription}>
                                  {mainRole.roles.length
                                    ? 'Selecting this group enables all permissions listed below.'
                                    : 'This group currently has no individual permissions to assign.'}
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>

                        <Box className={classes.permissionsList}>
                          {mainRole.roles.length ? (
                            <Box className={classes.permissionsListInner}>
                              {mainRole.roles.map((role) => {
                                const handleToggleRole = (value) => {
                                  form.change(`roles.${role.name}`, value);

                                  if (values.roles[mainRole.name] && !value) {
                                    form.change(`roles.${mainRole.name}`, false);
                                    return;
                                  }

                                  const updatedRoles = {
                                    ...values.roles,
                                    [role.name]: value,
                                  };

                                  const mainRoleSelected = mainRole.roles.every(
                                    (groupRole) => updatedRoles[groupRole.name]
                                  );

                                  if (mainRoleSelected && value) {
                                    form.change(`roles.${mainRole.name}`, true);
                                  }
                                };

                                return (
                                  <Checkbox
                                    key={role.name}
                                    name={`roles.${role.name}`}
                                    onChange={handleToggleRole}
                                    classes={{ root: classes.permissionCheckbox }}
                                    label={
                                      <Box className={classes.permissionLabel}>
                                        <Typography className={classes.permissionTitle}>
                                          {role.description}
                                        </Typography>
                                        <Typography className={classes.permissionCode}>
                                          {role.name}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography className={classes.permissionEmpty}>
                              No individual permissions are defined in this role group.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Dialog>
        );
      }}
    />
  );
};

export default SubUserRolesDialog;
